import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API, AuthContext } from '../App';
import axios from 'axios';
import Navbar from '../components/Navbar';
import CitySelect from '../components/CitySelect';
import { ArrowLeft, CheckCircle, Send, ShieldCheck, Lock } from 'lucide-react';
import { toast } from 'sonner';

const SERVICES = [
  'Home Loan', 'Personal Loan', 'Car Loan', 'Education Loan',
  'Business Loan', 'Credit Card', 'Savings Account', 'Fixed Deposit',
  'Mutual Funds', 'Insurance', 'Other'
];

const EMPLOYMENT = ['Salaried', 'Self-Employed', 'Business Owner', 'Freelancer', 'Other'];

const EnquiryPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const preselect = params.get('service');
  const { user } = useContext(AuthContext);
  // Capture marketing attribution from URL once on mount and persist for the session
  const utm = {
    utm_source: params.get('utm_source') || sessionStorage.getItem('utm_source') || null,
    utm_medium: params.get('utm_medium') || sessionStorage.getItem('utm_medium') || null,
    utm_campaign: params.get('utm_campaign') || sessionStorage.getItem('utm_campaign') || null,
    utm_content: params.get('utm_content') || sessionStorage.getItem('utm_content') || null,
  };
  useEffect(() => {
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'].forEach((k) => {
      const v = params.get(k);
      if (v) sessionStorage.setItem(k, v);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [form, setForm] = useState({
    name: '', email: '', phone: '',
    service_interested: preselect || 'Home Loan',
    bank_preference: '',
    loan_amount: '', annual_income: '',
    employment_type: 'Salaried',
    city: '', notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => { if (preselect) setForm((f) => ({ ...f, service_interested: preselect })); }, [preselect]);

  // Prefill name/email/phone from logged-in user
  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.full_name || '',
        email: f.email || user.email || '',
        phone: f.phone || user.phone || '',
      }));
    }
  }, [user]);

  // Auth gate: redirect to login if not authenticated
  if (!user) {
    const redirect = encodeURIComponent('/enquiry' + (preselect ? `?service=${encodeURIComponent(preselect)}` : ''));
    return (
      <div style={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}>
        <Navbar />
        <div style={{ maxWidth: 560, margin: '0 auto', padding: '5rem 1.5rem' }}>
          <div className="calculator-section" style={{ textAlign: 'center', padding: '3rem 2rem' }} data-testid="enquiry-login-gate">
            <Lock size={48} style={{ color: '#FF6B35', margin: '0 auto 1.25rem' }} />
            <h2 style={{ fontSize: '1.6rem', color: '#0F172A', marginBottom: '0.75rem' }}>Login to submit your enquiry</h2>
            <p style={{ color: '#475569', marginBottom: '1.75rem' }}>
              Quick enquiry is reserved for registered users — it helps us track your application status and connect you faster with the right bank advisor.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => navigate(`/?auth=login&redirect=${redirect}`)} className="primary-button" data-testid="enquiry-gate-login">Login</button>
              <button onClick={() => navigate(`/?auth=register&redirect=${redirect}`)} className="outline-button" data-testid="enquiry-gate-register">Create Account</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service_interested) {
      toast.error('Please fill name, email, phone & service');
      return;
    }
    if (!/^\d{10}$/.test(form.phone)) {
      toast.error('Enter a valid 10-digit phone');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        loan_amount: form.loan_amount ? Number(form.loan_amount) : null,
        annual_income: form.annual_income ? Number(form.annual_income) : null,
        utm_source: utm.utm_source,
        utm_medium: utm.utm_medium,
        utm_campaign: utm.utm_campaign,
        utm_content: utm.utm_content,
      };
      const token = localStorage.getItem('arthik_token');
      const res = await axios.post(`${API}/enquiries`, payload, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
      setDone(res.data);
      toast.success('Enquiry submitted! We\'ll reach out within 24 hours.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit enquiry');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div style={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}>
        <Navbar />
        <div style={{ maxWidth: 700, margin: '0 auto', padding: '4rem 1.5rem' }}>
          <div className="calculator-section" style={{ textAlign: 'center', padding: '3rem 2rem' }} data-testid="enquiry-success">
            <CheckCircle size={64} style={{ color: '#10B981', margin: '0 auto 1.5rem' }} />
            <h2 style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', color: '#0F172A', marginBottom: '0.5rem' }}>Enquiry Received</h2>
            <p style={{ color: '#475569', marginBottom: '1.5rem' }}>Reference: <span style={{ fontFamily: 'monospace', color: '#FF6B35', fontWeight: 700 }}>{done.id.slice(0, 8)}</span></p>
            <p style={{ color: '#475569', marginBottom: '2rem' }}>Our advisor will call you on <strong>{done.phone}</strong> within 24 hours.</p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button className="outline-button" onClick={() => navigate('/services')} data-testid="enquiry-explore">Explore Services</button>
              <button className="primary-button" onClick={() => navigate('/')} data-testid="enquiry-home">Back to Home</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}>
      <Navbar />
      <section style={{ background: 'linear-gradient(135deg, #FFF7ED 0%, #FFFFFF 100%)', padding: '4rem 1.5rem 2rem' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <button onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#FF6B35', cursor: 'pointer', marginBottom: '1.5rem', fontWeight: 600 }} data-testid="enquiry-back">
            <ArrowLeft size={18} /> Back
          </button>
          <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', color: '#0F172A', margin: 0 }}>Quick Loan Enquiry</h1>
          <p style={{ fontSize: '1.05rem', color: '#475569', marginTop: '0.5rem' }}>
            Tell us what you need — our advisor will call you back within 24 hours with best offers.
          </p>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: '0.75rem', padding: '0.4rem 0.8rem', background: '#ECFDF5', color: '#065F46', borderRadius: 999, fontSize: '0.85rem', fontWeight: 600 }}>
            <ShieldCheck size={14} /> 100% Free • No Spam
          </div>
        </div>
      </section>

      <section style={{ padding: '2rem 1.5rem 4rem' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <form className="calculator-section" onSubmit={submit} data-testid="enquiry-form" style={{ padding: '2rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem' }}>
              <div>
                <label className="label-text">Full Name *</label>
                <input className="input-field" value={form.name} onChange={handleChange('name')} placeholder="Your name" data-testid="enquiry-name" />
              </div>
              <div>
                <label className="label-text">Email *</label>
                <input className="input-field" type="email" value={form.email} onChange={handleChange('email')} placeholder="you@example.com" data-testid="enquiry-email" />
              </div>
              <div>
                <label className="label-text">Phone (10 digits) *</label>
                <input className="input-field" value={form.phone} onChange={handleChange('phone')} placeholder="9876543210" maxLength={10} data-testid="enquiry-phone" />
              </div>
              <div>
                <label className="label-text">Service *</label>
                <select className="input-field" value={form.service_interested} onChange={handleChange('service_interested')} data-testid="enquiry-service">
                  {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="label-text">Preferred Bank</label>
                <input className="input-field" value={form.bank_preference} onChange={handleChange('bank_preference')} placeholder="e.g., HDFC, SBI (optional)" data-testid="enquiry-bank" />
              </div>
              <div>
                <label className="label-text">City</label>
                <CitySelect value={form.city} onChange={(v) => setForm({ ...form, city: v })} testId="enquiry-city" />
              </div>
              <div>
                <label className="label-text">Loan Amount (₹)</label>
                <input className="input-field" type="number" value={form.loan_amount} onChange={handleChange('loan_amount')} placeholder="500000" data-testid="enquiry-amount" />
              </div>
              <div>
                <label className="label-text">Annual Income (₹)</label>
                <input className="input-field" type="number" value={form.annual_income} onChange={handleChange('annual_income')} placeholder="800000" data-testid="enquiry-income" />
              </div>
              <div>
                <label className="label-text">Employment</label>
                <select className="input-field" value={form.employment_type} onChange={handleChange('employment_type')} data-testid="enquiry-employment">
                  {EMPLOYMENT.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div style={{ marginTop: '1rem' }}>
              <label className="label-text">Notes (optional)</label>
              <textarea className="input-field" rows={3} value={form.notes} onChange={handleChange('notes')} placeholder="Anything else we should know?" data-testid="enquiry-notes" />
            </div>
            <button type="submit" disabled={submitting} className="primary-button" style={{ marginTop: '1.5rem', width: '100%', justifyContent: 'center', padding: '0.9rem 1rem', fontSize: '1rem' }} data-testid="enquiry-submit">
              <Send size={18} /> {submitting ? 'Submitting...' : 'Submit Enquiry'}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default EnquiryPage;
